import React, { useEffect, ReactNode, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import cntl from 'cntl';
import { CloseButton } from '../Buttons/CloseBtn/closeBtn';
import { useModalActions } from './useModalActions';
import { FocusTrap } from 'focus-trap-react';
import { ModalType, ModalVariant } from './ModalFactory';
import BackBtn from '../Buttons/BackBtn/BackBtn';
import './modal.css';
import { cubicBezier, Easing, Variants, motion } from 'framer-motion';

// Map your variants to your specific CSS animation classes
// Mimics your cubic-bezier(0.165, 0.840, 0.440, 1.000)
const customEase: Easing = cubicBezier(0.165, 0.84, 0.44, 1.0);

export const overlayVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, ease: customEase } },
  exit: { opacity: 0, transition: { duration: 0.5, ease: customEase } },
};

export const modalVariants: Record<string, Variants> = {
  // Replaces .two (Standard/Large/Form)
  standard: {
    hidden: { scale: 0.8, y: 100, opacity: 0 },
    visible: {
      scale: 1,
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: customEase },
    },
    exit: {
      scale: 0.8,
      y: 100,
      opacity: 0,
      transition: { duration: 0.5, ease: customEase },
    },
  },
  // Replaces .five roadRunnerRight (Drawer Right)
  'drawer-right': {
    hidden: { x: '120%', skewX: 0, scaleX: 1.3 },
    visible: {
      // Array mimics keyframes: 0%, 60%, 100%
      x: ['120%', '15%', '0%'],
      scaleX: [1.3, 0.5, 1],
      transition: { duration: 0.5, times: [0, 0.6, 1], ease: customEase },
    },
    exit: {
      x: ['0%', '10%', '120%'],
      scaleX: [1, 1, 1.6],
      transition: { duration: 0.5, times: [0, 0.4, 1], ease: customEase },
    },
  },
  'drawer-left': {
    // Inverted X coordinates for the left-side drawer
    hidden: { x: '-120%', skewX: 0, scaleX: 1.3 },
    visible: {
      x: ['-120%', '-15%', '0%'],
      scaleX: [1.3, 0.5, 1],
      transition: { duration: 0.5, times: [0, 0.6, 1], ease: customEase },
    },
    exit: {
      x: ['0%', '-10%', '-120%'],
      scaleX: [1, 1, 1.6],
      transition: { duration: 0.5, times: [0, 0.4, 1], ease: customEase },
    },
  },
  'date-picker': {
    hidden: { opacity: 0, y: -8, scale: 0.96, filter: 'blur(4px)' },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      filter: 'blur(0px)',
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 30,
        mass: 0.8,
      },
    },
    exit: {
      opacity: 0,
      y: -4,
      scale: 0.98,
      filter: 'blur(4px)',
      transition: { duration: 0.15, ease: 'easeOut' },
    },
  },
  // ... existing variants
  dialog: {
    hidden: { scale: 0.95, y: 15, opacity: 0 },
    visible: {
      scale: 1,
      y: 0,
      opacity: 1,
      transition: { duration: 0.3, ease: customEase },
    },
    exit: {
      scale: 0.95,
      y: 10,
      opacity: 0,
      transition: { duration: 0.2, ease: customEase },
    },
  },
};

const variantClasses: Record<ModalVariant, string> = {
  'date-picker':
    'md:w-[360px] md:h-auto rounded-[var(--radius-artisan)] overflow-hidden', // Adjusted for popover
  'drawer-right':
    'md:w-[350px] md:h-full md:ml-auto md:rounded-l-lg overflow-visible',
  'drawer-left':
    'md:w-[350px] md:h-full md:mr-auto md:rounded-r-lg overflow-visible',
  standard: 'md:w-[600px] md:h-auto md:max-h-[85vh] md:rounded-lg',
  large: 'md:w-[900px] md:h-[80vh] md:rounded-lg',
  form: 'w-full md:w-[550px] h-full md:h-[80vh] md:rounded-lg',
  dialog:
    'w-[75%] md:w-[420px] md:h-auto rounded-[var(--radius-artisan)] overflow-hidden text-center self-center',
};

export const Modal: ModalType<any> = ({
  id,
  children,
  key: modalKey,
  variant = 'standard',
  title = '',
  ariaLabel,
  className = '',
  darkenOverlay = true,
  requestModalClose,
  ...props
}) => {
  const { modalData } = useModalActions(modalKey);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Extract anchor coordinates if this is a popover
  const anchorRect = (modalData as any)?.anchorRect as DOMRect | undefined;
  const isPopover = variant === 'date-picker' && !!anchorRect;

  // Map your variant prop to the specific motion variants we defined
  const activeVariants = modalVariants[variant] || modalVariants.standard;

  // 1. Consolidated close handler
  const handleClose = () => {
    requestModalClose();
  };

  useEffect(() => {
    // 1. Calculate scrollbar width to prevent "jumping"
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    // 2. Lock scroll and compensate for missing scrollbar
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // 3. Save focus for accessibility
    triggerRef.current = document.activeElement as HTMLElement;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };

    window.addEventListener('keydown', handleEsc);

    return () => {
      // 4. Cleanup: Reset everything when the modal unmounts
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
      window.removeEventListener('keydown', handleEsc);

      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    };
  }, []); // Empty dependency array because AnimatePresence handles the mount/unmount

  const isFrosted = ['form', 'drawer', 'standard', 'large', 'dialog'].some(
    (s) => variant.includes(s),
  );

  // Make overlay transparent and remove flex-centering if it's a popover
  const overlayCN = cntl`
    modal-background
    fixed inset-0 z-[100]
    flex items-center justify-center
    ${isFrosted && !isPopover ? (darkenOverlay ? 'bg-black/50 backdrop-blur-[2px]' : 'bg-black/10 backdrop-blur-[2px]') : 'bg-transparent'}
  `;

  // Calculate dynamic position based on the trigger button's coordinates
  // Inside your Modal component in modal.tsx
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const popoverStyle: React.CSSProperties =
    !isPopover || isMobile
      ? {}
      : {
          position: 'absolute',
          top: `${anchorRect!.bottom + 8}px`,
          left: `${anchorRect!.left}px`,
          transformOrigin: 'top left',
          maxWidth: '400px', // Prevents extra-wide forms from bleeding right
        };

  const containerCN = cntl`
    modal 
    relative z-[101] flex flex-col shadow-2xl bg-brand-cream
    ${variantClasses[variant]}
  `;

  const contentCN = cntl`
  custom-scrollbar
   overflow-y-scroll 
   overflow-x-hidden
   p-6
   h-full
   md:h-[unset]
  ${className}
  `;

  const header = useMemo(() => {
    // Using a Fragment or a simple array is fine,
    // but pushing to an array requires keys for React.
    const hElems: ReactNode[] = [];

    if (variant === 'form') {
      hElems.push(
        <CloseButton
          key="modal-close" // Added Key
          onClick={requestModalClose}
          className="absolute top-[15px] right-[15px] z-110"
        />,
      );
    }

    if (variant === 'drawer-left' || (isMobile && isPopover)) {
      hElems.push(
        <BackBtn
          key="modal-close" // Added Key
          onClick={requestModalClose}
          className="absolute top-[15px] right-[15px] z-110"
        />,
      );
    }

    if (variant === 'drawer-right') {
      hElems.push(
        <BackBtn
          key="modal-close" // Added Key
          onClick={requestModalClose}
          className="absolute top-[10px] left-[10px] md:top-[15px] md:left-[15px] z-110"
          reverse
        />,
      );
    }

    if (title) {
      hElems.push(
        <h2 className="pt-6 md:pt-8 mb-6" key="modal-title">
          {title}
        </h2>,
      ); // Added Key
    }

    // No need to .map() an array that already contains ReactNodes
    return (
      <div className="w-full flex items-center justify-center">{hElems}</div>
    );
  }, [title, variant, requestModalClose]); // Added requestModalClose to dependencies

  return createPortal(
    <div id="modal-container">
      <fieldset>
        {/* 2. Animate the Overlay */}
        <motion.div
          className={overlayCN}
          onClick={handleClose}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <FocusTrap // 3. Deactivate trap during exit animation
            active={true}
            focusTrapOptions={{
              // 4. Fallback focus prevents crashes if children have no focusable elements
              fallbackFocus: '#modal-dialog-content',
              // Allows users to click the overlay to close without the trap fighting them
              clickOutsideDeactivates: true,
            }}
          >
            {/* 3. Animate the Container */}
            <motion.div
              id="modal-dialog-content"
              tabIndex={-1}
              className={containerCN}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              style={popoverStyle}
              variants={activeVariants} // Apply variant-specific physics
              // Let the parent motion.div cascade the initial/animate/exit commands down
            >
              {header}
              <div className={contentCN}>
                {children({
                  ...props,
                  modalData,
                  requestModalClose,
                })}
              </div>
            </motion.div>
          </FocusTrap>
        </motion.div>
      </fieldset>
    </div>,
    document.body,
  );
};
